import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { KeyChord } from 'vscode/vscode/vs/base/common/keyCodes';
import { EditorContextKeys } from 'vscode/vscode/vs/editor/common/editorContextKeys';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { CONTEXT_ACCESSIBILITY_MODE_ENABLED } from 'vscode/vscode/vs/platform/accessibility/common/accessibility';
import { registerAction2, MenuId, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { InputFocusedContextKey } from 'vscode/vscode/vs/platform/contextkey/common/contextkeys';
import { CTX_INLINE_CHAT_FOCUSED, CTX_INLINE_CHAT_INNER_CURSOR_FIRST, CTX_INLINE_CHAT_INNER_CURSOR_LAST, CTX_INLINE_CHAT_RESPONSE_TYPES, CTX_INLINE_CHAT_LAST_RESPONSE_TYPE, CTX_INLINE_CHAT_HAS_PROVIDER } from 'vscode/vscode/vs/workbench/contrib/inlineChat/common/inlineChat';
import { CTX_NOTEBOOK_CELL_CHAT_FOCUSED, MENU_CELL_CHAT_INPUT, CTX_NOTEBOOK_CHAT_HAS_ACTIVE_REQUEST, MENU_CELL_CHAT_WIDGET, CTX_NOTEBOOK_CHAT_USER_DID_EDIT, CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION, MENU_CELL_CHAT_WIDGET_STATUS, MENU_CELL_CHAT_WIDGET_FEEDBACK } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/chat/notebookChatContext';
import { NotebookChatController } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/chat/notebookChatController';
import { NotebookAction, NotebookCellAction, getEditorFromArgsOrActivePane, CELL_TITLE_CELL_GROUP_ID } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/coreActions';
import { insertNewCell } from '../insertCellActions.js';
import { CellEditState } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { CellKind, NOTEBOOK_EDITOR_CURSOR_BOUNDARY, NotebookSetting } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { NOTEBOOK_CELL_EDITOR_FOCUSED, NOTEBOOK_EDITOR_FOCUSED, NOTEBOOK_EDITOR_EDITABLE, NOTEBOOK_CELL_GENERATED_BY_CHAT } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';

const _moduleId = "vs/workbench/contrib/notebook/browser/controller/chat/cellChatActions";
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.accept',
            title: ( localize2WithPath(_moduleId, 0, "Make Request")),
            icon: Codicon.send,
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    CTX_NOTEBOOK_CELL_CHAT_FOCUSED,
                    CTX_INLINE_CHAT_FOCUSED,
                     (NOTEBOOK_CELL_EDITOR_FOCUSED.negate())
                ))),
                weight: 200 ,
                primary: 3
            },
            menu: {
                id: MENU_CELL_CHAT_INPUT,
                group: 'navigation',
                order: 1,
                when: ( (CTX_NOTEBOOK_CHAT_HAS_ACTIVE_REQUEST.negate()))
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.acceptInput();
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.arrowOutUp',
            title: ( localizeWithPath(_moduleId, 1, 'Cursor Up')),
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    CTX_NOTEBOOK_CELL_CHAT_FOCUSED,
                    CTX_INLINE_CHAT_FOCUSED,
                    CTX_INLINE_CHAT_INNER_CURSOR_FIRST,
                     (NOTEBOOK_CELL_EDITOR_FOCUSED.negate()),
                     (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate())
                ))),
                weight: 0  + 7,
                primary: 2048  | 16
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        const editor = context.notebookEditor;
        const activeCell = context.cell;
        const idx = editor.getCellIndex(activeCell);
        if (typeof idx !== 'number') {
            return;
        }
        if (idx < 1 || editor.getLength() === 0) {
            return;
        }
        const newCell = editor.cellAt(idx - 1);
        const newFocusMode = newCell.cellKind === CellKind.Markup && newCell.getEditState() === CellEditState.Preview ? 'container' : 'editor';
        const focusEditorLine = newCell.textBuffer.getLineCount();
        await editor.focusNotebookCell(newCell, newFocusMode, { focusEditorLine: focusEditorLine });
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.arrowOutDown',
            title: ( localizeWithPath(_moduleId, 2, 'Cursor Down')),
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    CTX_NOTEBOOK_CELL_CHAT_FOCUSED,
                    CTX_INLINE_CHAT_FOCUSED,
                    CTX_INLINE_CHAT_INNER_CURSOR_LAST,
                     (NOTEBOOK_CELL_EDITOR_FOCUSED.negate()),
                     (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate())
                ))),
                weight: 0  + 7,
                primary: 2048  | 18
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        await NotebookChatController.get(context.notebookEditor)?.focusNext();
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: 'notebook.cell.focusChatWidget',
            title: ( localizeWithPath(_moduleId, 3, 'Focus Chat Widget')),
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    NOTEBOOK_EDITOR_FOCUSED,
                     (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate()),
                     (ContextKeyExpr.and(
                         (ContextKeyExpr.has(InputFocusedContextKey)),
                        EditorContextKeys.editorTextFocus,
                         (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('bottom')),
                         (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('none'))
                    )),
                     (EditorContextKeys.isEmbeddedDiffEditor.negate())
                ))),
                weight: 0  + 7,
                primary: 2048  | 16
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        const index = context.notebookEditor.getCellIndex(context.cell);
        await NotebookChatController.get(context.notebookEditor)?.focusNearestWidget(index, 'above');
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: 'notebook.cell.focusNextChatWidget',
            title: ( localizeWithPath(_moduleId, 4, 'Focus Next Cell Chat Widget')),
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    NOTEBOOK_EDITOR_FOCUSED,
                     (CONTEXT_ACCESSIBILITY_MODE_ENABLED.negate()),
                     (ContextKeyExpr.and(
                         (ContextKeyExpr.has(InputFocusedContextKey)),
                        EditorContextKeys.editorTextFocus,
                         (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('top')),
                         (NOTEBOOK_EDITOR_CURSOR_BOUNDARY.notEqualsTo('none'))
                    )),
                     (EditorContextKeys.isEmbeddedDiffEditor.negate())
                ))),
                weight: 0  + 7,
                primary: 2048  | 18
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        const index = context.notebookEditor.getCellIndex(context.cell);
        await NotebookChatController.get(context.notebookEditor)?.focusNearestWidget(index, 'below');
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.stop',
            title: ( localize2WithPath(_moduleId, 5, "Stop Request")),
            icon: Codicon.debugStop,
            menu: {
                id: MENU_CELL_CHAT_INPUT,
                group: 'navigation',
                order: 1,
                when: CTX_NOTEBOOK_CHAT_HAS_ACTIVE_REQUEST
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.cancelCurrentRequest(false);
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.close',
            title: ( localize2WithPath(_moduleId, 6, "Close Chat")),
            icon: Codicon.close,
            menu: {
                id: MENU_CELL_CHAT_WIDGET,
                group: 'navigation',
                order: 2
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.dismiss(false);
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.acceptChanges',
            title: ( localize2WithPath(_moduleId, 7, "Accept Changes")),
            shortTitle: ( localizeWithPath(_moduleId, 8, 'Accept')),
            icon: Codicon.check,
            tooltip: ( localizeWithPath(_moduleId, 9, 'Accept Changes')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        CTX_NOTEBOOK_CELL_CHAT_FOCUSED,
                        CTX_INLINE_CHAT_FOCUSED,
                         (NOTEBOOK_CELL_EDITOR_FOCUSED.negate())
                    ))),
                    weight: 100  + 10,
                    primary: 2048  | 3 ,
                },
                {
                    when: ( (ContextKeyExpr.and(
                        CTX_NOTEBOOK_CELL_CHAT_FOCUSED,
                        CTX_INLINE_CHAT_FOCUSED,
                        CTX_NOTEBOOK_CHAT_USER_DID_EDIT,
                         (NOTEBOOK_CELL_EDITOR_FOCUSED.negate())
                    ))),
                    weight: 0  + 10,
                    primary: 9
                },
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                        ContextKeyExpr.not(InputFocusedContextKey),
                         (NOTEBOOK_CELL_EDITOR_FOCUSED.negate()),
                         (CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION.isEqualTo('below'))
                    ))),
                    primary: 2048  | 3 ,
                    weight: 200
                }
            ],
            menu: [
                {
                    id: MENU_CELL_CHAT_WIDGET_STATUS,
                    group: 'inline',
                    order: 0,
                    when: ( (CTX_INLINE_CHAT_RESPONSE_TYPES.notEqualsTo("onlyMessages" ))),
                }
            ],
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.acceptSession();
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.discard',
            title: ( localizeWithPath(_moduleId, 10, 'Discard')),
            icon: Codicon.discard,
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    CTX_NOTEBOOK_CELL_CHAT_FOCUSED,
                    CTX_INLINE_CHAT_FOCUSED,
                     (CTX_NOTEBOOK_CHAT_USER_DID_EDIT.negate()),
                     (NOTEBOOK_CELL_EDITOR_FOCUSED.negate())
                ))),
                weight: 100 ,
                primary: 9
            },
            menu: {
                id: MENU_CELL_CHAT_WIDGET_STATUS,
                group: 'main',
                order: 1
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.discard();
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.feedbackHelpful',
            title: ( localizeWithPath(_moduleId, 11, 'Helpful')),
            icon: Codicon.thumbsup,
            menu: {
                id: MENU_CELL_CHAT_WIDGET_FEEDBACK,
                group: 'inline',
                order: 1,
                when: ( (CTX_INLINE_CHAT_LAST_RESPONSE_TYPE.notEqualsTo(undefined))),
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.feedbackLast(1 );
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.feedbackUnhelpful',
            title: ( localizeWithPath(_moduleId, 12, 'Unhelpful')),
            icon: Codicon.thumbsdown,
            menu: {
                id: MENU_CELL_CHAT_WIDGET_FEEDBACK,
                group: 'inline',
                order: 2,
                when: ( (CTX_INLINE_CHAT_LAST_RESPONSE_TYPE.notEqualsTo(undefined))),
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.feedbackLast(0 );
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.reportIssueForBug',
            title: ( localizeWithPath(_moduleId, 13, 'Report Issue')),
            icon: Codicon.report,
            menu: {
                id: MENU_CELL_CHAT_WIDGET_FEEDBACK,
                group: 'inline',
                order: 3,
                when: ( (CTX_INLINE_CHAT_LAST_RESPONSE_TYPE.notEqualsTo(undefined))),
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.feedbackLast(4 );
    }
});
async function startChat(accessor, context, index, input, autoSend, source) {
    const configurationService = accessor.get(IConfigurationService);
    const commandService = accessor.get(ICommandService);
    if (configurationService.getValue(NotebookSetting.cellChat)) {
        context.notebookEditor.focusContainer();
        NotebookChatController.get(context.notebookEditor)?.run(index, input, autoSend);
    }
    else if (configurationService.getValue(NotebookSetting.cellGenerate)) {
        const activeCell = context.notebookEditor.getActiveCell();
        const targetCell = activeCell?.getTextLength() === 0 && source !== 'insertToolbar' ? activeCell : (await insertNewCell(accessor, context, CellKind.Code, 'below'));
        if (targetCell) {
            targetCell.enableAutoLanguageDetection();
            await context.notebookEditor.revealFirstLineIfOutsideViewport(targetCell);
            const codeEditor = context.notebookEditor.codeEditors.find(ce => ce[0] === targetCell)?.[1];
            if (codeEditor) {
                codeEditor.focus();
                commandService.executeCommand('inlineChat.start');
            }
        }
    }
}
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.start',
            title: {
                value: '$(sparkle) ' + ( localizeWithPath(_moduleId, 14, "Generate")),
                original: '$(sparkle) Generate',
            },
            tooltip: ( localizeWithPath(_moduleId, 15, "Start Chat to Generate Code")),
            metadata: {
                description: ( localizeWithPath(_moduleId, 15, "Start Chat to Generate Code")),
                args: [
                    {
                        name: 'args',
                        schema: {
                            type: 'object',
                            required: ['index'],
                            properties: {
                                'index': {
                                    type: 'number'
                                },
                                'input': {
                                    type: 'string'
                                },
                                'autoSend': {
                                    type: 'boolean'
                                }
                            }
                        }
                    }
                ]
            },
            f1: false,
            keybinding: {
                when: ( (ContextKeyExpr.and(
                    NOTEBOOK_EDITOR_FOCUSED,
                     (NOTEBOOK_EDITOR_EDITABLE.isEqualTo(true)),
                    ContextKeyExpr.not(InputFocusedContextKey),
                    CTX_INLINE_CHAT_HAS_PROVIDER,
                     (ContextKeyExpr.or(
                         (ContextKeyExpr.equals(`config.${NotebookSetting.cellChat}`, true)),
                         (ContextKeyExpr.equals(`config.${NotebookSetting.cellGenerate}`, true))
                    ))
                ))),
                weight: 200 ,
                primary: 2048  | 39 ,
                secondary: [KeyChord(2048  | 41 , 39 )],
            },
            menu: [
                {
                    id: MenuId.NotebookCellBetween,
                    group: 'inline',
                    order: -1,
                    when: ( (ContextKeyExpr.and(
                         (NOTEBOOK_EDITOR_EDITABLE.isEqualTo(true)),
                        CTX_INLINE_CHAT_HAS_PROVIDER,
                         (ContextKeyExpr.or(
                             (ContextKeyExpr.equals(`config.${NotebookSetting.cellChat}`, true)),
                             (ContextKeyExpr.equals(`config.${NotebookSetting.cellGenerate}`, true))
                        ))
                    )))
                }
            ]
        });
    }
    getEditorContextFromArgsOrActive(accessor, ...args) {
        const [firstArg] = args;
        if (!firstArg) {
            const notebookEditor = getEditorFromArgsOrActivePane(accessor);
            if (!notebookEditor) {
                return undefined;
            }
            const activeCell = notebookEditor.getActiveCell();
            if (!activeCell) {
                return undefined;
            }
            return {
                cell: activeCell,
                notebookEditor,
                input: undefined,
                autoSend: undefined
            };
        }
        if (typeof firstArg !== 'object' || typeof firstArg.index !== 'number') {
            return undefined;
        }
        const notebookEditor = getEditorFromArgsOrActivePane(accessor);
        if (!notebookEditor) {
            return undefined;
        }
        const cell = firstArg.index <= 0 ? undefined : notebookEditor.cellAt(firstArg.index - 1);
        return {
            cell,
            notebookEditor,
            input: firstArg.input,
            autoSend: firstArg.autoSend
        };
    }
    async runWithContext(accessor, context) {
        const index = Math.max(0, context.cell ? context.notebookEditor.getCellIndex(context.cell) + 1 : 0);
        await startChat(accessor, context, index, context.input, context.autoSend, context.source);
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.startAtTop',
            title: {
                value: '$(sparkle) ' + ( localizeWithPath(_moduleId, 14, "Generate")),
                original: '$(sparkle) Generate',
            },
            tooltip: ( localizeWithPath(_moduleId, 15, "Start Chat to Generate Code")),
            f1: false,
            menu: [
                {
                    id: MenuId.NotebookCellListTop,
                    group: 'inline',
                    order: -1,
                    when: ( (ContextKeyExpr.and(
                         (NOTEBOOK_EDITOR_EDITABLE.isEqualTo(true)),
                        CTX_INLINE_CHAT_HAS_PROVIDER,
                         (ContextKeyExpr.or(
                             (ContextKeyExpr.equals(`config.${NotebookSetting.cellChat}`, true)),
                             (ContextKeyExpr.equals(`config.${NotebookSetting.cellGenerate}`, true))
                        ))
                    )))
                },
            ]
        });
    }
    async runWithContext(accessor, context) {
        await startChat(accessor, context, 0, '', false);
    }
});
MenuRegistry.appendMenuItem(MenuId.NotebookToolbar, {
    command: {
        id: 'notebook.cell.chat.start',
        icon: Codicon.sparkle,
        title: ( localizeWithPath(_moduleId, 16, "Generate")),
        tooltip: ( localizeWithPath(_moduleId, 17, "Start Chat to Generate Code"))
    },
    order: -10,
    group: 'navigation/add',
    when: ( (ContextKeyExpr.and(
         (NOTEBOOK_EDITOR_EDITABLE.isEqualTo(true)),
         (ContextKeyExpr.notEquals('config.notebook.insertToolbarLocation', 'betweenCells')),
         (ContextKeyExpr.notEquals('config.notebook.insertToolbarLocation', 'hidden')),
        CTX_INLINE_CHAT_HAS_PROVIDER,
         (ContextKeyExpr.or(
             (ContextKeyExpr.equals(`config.${NotebookSetting.cellChat}`, true)),
             (ContextKeyExpr.equals(`config.${NotebookSetting.cellGenerate}`, true))
        ))
    )))
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.focus',
            title: ( localizeWithPath(_moduleId, 18, 'Focus Chat')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                        ContextKeyExpr.not(InputFocusedContextKey),
                         (CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION.isEqualTo('above'))
                    ))),
                    primary: 2048  | 18 ,
                    weight: 200
                },
                {
                    when: ( (ContextKeyExpr.and(
                        NOTEBOOK_EDITOR_FOCUSED,
                        ContextKeyExpr.not(InputFocusedContextKey),
                         (CTX_NOTEBOOK_CHAT_OUTER_FOCUS_POSITION.isEqualTo('below'))
                    ))),
                    primary: 2048  | 16 ,
                    weight: 200
                }
            ],
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.focus();
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.focusNextCell',
            title: ( localizeWithPath(_moduleId, 19, 'Focus Next Cell')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(CTX_NOTEBOOK_CELL_CHAT_FOCUSED, CTX_INLINE_CHAT_FOCUSED))),
                    primary: 2048  | 18 ,
                    weight: 200
                }
            ],
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.focusNext();
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.focusPreviousCell',
            title: ( localizeWithPath(_moduleId, 20, 'Focus Previous Cell')),
            keybinding: [
                {
                    when: ( (ContextKeyExpr.and(CTX_NOTEBOOK_CELL_CHAT_FOCUSED, CTX_INLINE_CHAT_FOCUSED))),
                    primary: 2048  | 16 ,
                    weight: 200
                }
            ],
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.focusAbove();
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.previousFromHistory',
            title: ( localize2WithPath(_moduleId, 21, "Previous From History")),
            precondition: ( (ContextKeyExpr.and(CTX_NOTEBOOK_CELL_CHAT_FOCUSED, CTX_INLINE_CHAT_FOCUSED))),
            keybinding: {
                when: ( (ContextKeyExpr.and(CTX_NOTEBOOK_CELL_CHAT_FOCUSED, CTX_INLINE_CHAT_FOCUSED))),
                weight: 0  + 10,
                primary: 16 ,
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.populateHistory(true);
    }
});
registerAction2(class extends NotebookAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.nextFromHistory',
            title: ( localize2WithPath(_moduleId, 22, "Next From History")),
            precondition: ( (ContextKeyExpr.and(CTX_NOTEBOOK_CELL_CHAT_FOCUSED, CTX_INLINE_CHAT_FOCUSED))),
            keybinding: {
                when: ( (ContextKeyExpr.and(CTX_NOTEBOOK_CELL_CHAT_FOCUSED, CTX_INLINE_CHAT_FOCUSED))),
                weight: 0  + 10,
                primary: 18
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        NotebookChatController.get(context.notebookEditor)?.populateHistory(false);
    }
});
registerAction2(class extends NotebookCellAction {
    constructor() {
        super({
            id: 'notebook.cell.chat.restore',
            title: ( localize2WithPath(_moduleId, 23, "Generate")),
            icon: Codicon.sparkle,
            menu: {
                id: MenuId.NotebookCellTitle,
                group: CELL_TITLE_CELL_GROUP_ID,
                order: 0,
                when: ( (ContextKeyExpr.and(
                     (NOTEBOOK_EDITOR_EDITABLE.isEqualTo(true)),
                    CTX_INLINE_CHAT_HAS_PROVIDER,
                    NOTEBOOK_CELL_GENERATED_BY_CHAT,
                     (ContextKeyExpr.equals(`config.${NotebookSetting.cellChat}`, true))
                )))
            },
            f1: false
        });
    }
    async runWithContext(accessor, context) {
        const cell = context.cell;
        if (!cell) {
            return;
        }
        const notebookEditor = context.notebookEditor;
        const controller = NotebookChatController.get(notebookEditor);
        if (!controller) {
            return;
        }
        const prompt = controller.getPromptFromCache(cell);
        if (prompt) {
            controller.restore(cell, prompt);
        }
    }
});
